import React from 'react'
import { useSelector } from 'react-redux'

import { FeatureTooltip, Flex, Text } from '@driftt/dds'

import { ActionType } from 'components/ActivityActions'
import { ActionableContactListItem } from 'components/Contacts/ActionableContactListItem'
import { driftErrorStateBuilder, driftLoadingStateBuilder } from 'components/States'
import { useCadenceContacts } from 'hooks/useCadenceContacts'
import usePresenceSubscription from 'hooks/usePresenceSubscription'
import { getCurrentOrg } from 'selectors'
import { contactPath } from 'utils/paths'

import { DateRange } from './utils'

interface ContactsPopupProps {
  numContacts: number
  accountId: string
  dateRange: DateRange
}

const DEFAULT_ACTION_TYPES = [ActionType.EMAIL, ActionType.LINKEDIN, ActionType.SALESLOFT]

export const ContactsPopup = ({ numContacts, accountId, dateRange }: ContactsPopupProps) => {
  const { data, isLoading, error } = useCadenceContacts(accountId, dateRange)

  const org = useSelector(getCurrentOrg)
  const contacts = data ?? []
  const presentEndUserIds = usePresenceSubscription(org.id, contacts)

  const LoadingComponent = driftLoadingStateBuilder('Loading contacts...')
  const ErrorComponent = driftErrorStateBuilder('enrolled contacts')

  return (
    <FeatureTooltip.Root>
      <FeatureTooltip.Trigger css={{ cursor: 'pointer' }}>
        <Text variant="metadata">{`${numContacts} contacts`}</Text>
      </FeatureTooltip.Trigger>
      <FeatureTooltip.Content orientation="horizontal" side="right">
        <Flex
          direction="column"
          css={{ maxHeight: '420px', overflowY: 'scroll', padding: '0 8px' }}>
          {isLoading && <LoadingComponent />}
          {error !== null && <ErrorComponent />}
          {data &&
            data.map((endUserData, index) => (
              <ActionableContactListItem
                key={index}
                endUserData={endUserData}
                userPresence={presentEndUserIds.has(endUserData.id) ? 'available' : 'away'}
                onClick={() => window.open(contactPath(endUserData.id), '_blank')}
                actionTypes={
                  presentEndUserIds.has(endUserData.id)
                    ? [ActionType.CHAT, ...DEFAULT_ACTION_TYPES]
                    : DEFAULT_ACTION_TYPES
                }
              />
            ))}
        </Flex>
      </FeatureTooltip.Content>
    </FeatureTooltip.Root>
  )
}
